"use client";

// React & Nextjs imports
import Link from "next/link";
import Image from "next/image";
import React, { memo } from "react";
import { useRouter } from "next/navigation";

// Models & Constants
import { UsStock } from "@/app/interface/global";

// Hooks & Utils & Contexts
import { useCulture } from "@/context/cultureProvider";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { formatCurrency } from "@/app/utils/currency-formatter";

// UI Components
// import { Badge, BadgeScheme, BadgeVariant } from "@/stories/badge/badge";
import { ButtonSize, ButtonVariant } from "@/stories/button/button";
import { IconButton } from "@/stories/icon-button/icon-button";

// Icons
// import TopPicks from "@/app/assets/icons/top-picks";
import ArrowRight from "@/app/assets/icons/arrow-right";
import { DirectPaths } from "@/config/routes";
import { useAuth } from "@/context/authcontext";
import { productRoute, ProductRoute } from "@/types/product";
// import { useProductPriceQuery } from "../use-us-stocks";

interface Props {
    data: UsStock[] | undefined;
    pageRoute?: ProductRoute;
}

/* const ProductPriceComponent: React.FC<{ symbol: string }> = ({ symbol }) => {
    const { cultureContext } = useCulture();

    const { productPrice, isproductPriceLoading } = useProductPriceQuery({ symbol });

    return (
        <>
            {isproductPriceLoading ? (
                <span className="mt-0.5 inline-block h-4 w-s22 animate-pulse rounded-sm bg-gray-200"></span>
            ) : (
                <>
                    {formatCurrency(
                        productPrice?.data?.price ?? 0,
                        cultureContext.currencyCode,
                        cultureContext.locale,
                    )}
                </>
            )}
        </>
    );
}; */

const UsStocksHeroCard: React.FC<Props> = ({ data, pageRoute }) => {
    const router = useRouter();
    const { isAuthenticated } = useAuth();
    const { cultureContext } = useCulture();

    const linkFormat = (name: string) => {
        return name.replace(/\s+/gm, "-").toLowerCase() || "";
    };

    const handleRedirect = (event: React.MouseEvent, data: UsStock) => {
        event.preventDefault();

        const companyName = linkFormat(data?.company_name);

        const preLoginStocksUrl = `${process.env.ICM_GLOBAL_URL}${pageRoute === productRoute["us-stocks"] ? DirectPaths.US_STOCKS : (pageRoute === productRoute["us-etfs"] && DirectPaths.US_ETFS) || ""}/${data?.symbol}/${companyName}`;

        const postLoginStocksUrl = `${process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL}${pageRoute === productRoute["us-stocks"] ? DirectPaths.US_STOCKS : (pageRoute === productRoute["us-etfs"] && DirectPaths.US_ETFS) || ""}/${data?.symbol}/${companyName}`;

        const redirectUrl = `${isAuthenticated ? postLoginStocksUrl : preLoginStocksUrl}`;
        const eventName = `US_Stock_Page_View_Details_${companyName}`;

        setAnalyticsEvent(redirectUrl, eventName ?? "");
        router.push(redirectUrl);
    };

    return (
        <>
            {data?.length ? (
                <>
                    {data?.map((data, index) => {
                        const companyName = linkFormat(data?.company_name);

                        return (
                            <Link
                                className="bg-red border-black/10 max-h-[132px] max-w-[475px] cursor-pointer rounded-xl border p-s7 md:w-[442px] md:p-s9"
                                onClick={(e) => {
                                    handleRedirect(e, data);
                                }}
                                href={
                                    isAuthenticated
                                        ? `${process.env.ICM_GLOBAL_URL}${pageRoute === productRoute["us-stocks"] ? DirectPaths.US_STOCKS : (pageRoute === productRoute["us-etfs"] && DirectPaths.US_ETFS) || ""}/${data?.symbol}/${companyName}`
                                        : `${process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL}${pageRoute === productRoute["us-stocks"] ? DirectPaths.US_STOCKS : (pageRoute === productRoute["us-etfs"] && DirectPaths.US_ETFS) || ""}/${data?.symbol}/${companyName}`
                                }
                                key={index}
                            >
                                <div className="flex items-center gap-4">
                                    {/* Image container with fixed dimensions */}
                                    <div className="h-12 w-12 flex-shrink-0 animate-pulse overflow-hidden rounded-lg bg-[#0E0F171A] md:h-[84px] md:w-[84px]">
                                        {data.company_logo ? (
                                            <Image
                                                src={data.company_logo}
                                                alt="Logo"
                                                width={84}
                                                height={84}
                                                className="h-full w-full rounded-lg object-cover opacity-0 transition-all duration-200" /* border-[1px] border-neutralOpacityDark-10 */
                                                onLoad={(obj) => {
                                                    obj.currentTarget.classList.add("opacity-100");
                                                    if (
                                                        obj.currentTarget.parentElement?.classList
                                                    ) {
                                                        obj.currentTarget.parentElement.classList.remove(
                                                            "animate-pulse",
                                                        );
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-3xl">
                                                {data.company_name[0]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Content wrapper with proper flex behavior */}
                                    <div className="flex min-w-0 flex-1 items-center justify-between">
                                        {/* Text content with proper truncation */}
                                        <div className="flex min-w-0 max-w-[calc(100%-60px)] flex-col gap-[8px] md:max-w-[calc(100%-80px)]">
                                            <p className="-mb-0.5 text-lg font-semibold leading-[130%] tracking-[0%]">
                                                {data.symbol}
                                            </p>
                                            <div className="overflow-hidden">
                                                <h3 className="m-0 truncate text-left text-md font-semibold leading-[23.4px] md:text-lg">
                                                    {data.company_name}
                                                </h3>
                                            </div>
                                            <div className="-mt-2 whitespace-nowrap text-left md:-mt-0">
                                                <>
                                                    <span className="text-sm font-medium md:text-md">
                                                        {formatCurrency(
                                                            data.live_price ?? 0,
                                                            cultureContext.currencyCode,
                                                            cultureContext.locale,
                                                        )}
                                                        {/* <ProductPriceComponent
                                                            symbol={data.symbol}
                                                        /> */}
                                                    </span>{" "}
                                                    {/* <span className="text-sm text-neutral-300">
                                                        •
                                                    </span>{" "}
                                                    <span className="text-xs leading-[140%] text-neutral-300 md:text-sm">
                                                        {`${data.minUnits} ${data.minUnits > 1 ? "shares" : "share"} min`}
                                                    </span> */}
                                                </>

                                                {/* <span className="flex items-center rounded-[8px] py-1 text-xs">
                                                    {data.symbol && (
                                                        <Badge
                                                            label={data.symbol}
                                                            showIcon={false}
                                                            icon={<TopPicks />}
                                                            variant={BadgeVariant.Success}
                                                            scheme={BadgeScheme.Light}
                                                            showCross={false}
                                                        />
                                                    )}
                                                </span> */}
                                            </div>
                                        </div>

                                        {/* Button with fixed width */}
                                        <div className="flex-shrink-0">
                                            <IconButton
                                                icon={<ArrowRight />}
                                                className="!min-h-9 !min-w-9 border border-[#0E0F171A] focus:right-0 focus:outline-none"
                                                size={ButtonSize.Small}
                                                variant={ButtonVariant.SecondaryDefault}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </>
            ) : null}
        </>
    );
};

export default memo(UsStocksHeroCard);
